/**
 * Toast — Radix-backed transient notifications (Claude Design `.toast`).
 *
 * Bottom-center viewport; each toast is a `surface-3` card with a strong border,
 * a large shadow, a tone-colored 24px icon tile, the message, and a close button.
 *
 * Exposes:
 *   - <ToastProvider> — wrap the app (or a subtree) once. Renders the viewport.
 *   - useToast() — returns `toast({ title, description, tone })` to push toasts.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';

import { cn } from './cn.js';

export type ToastTone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info';

export interface ToastOptions {
  title: string;
  description?: ReactNode;
  tone?: ToastTone;
  /** Auto-dismiss after N ms (default 5000). */
  duration?: number;
}

interface ToastEntry extends ToastOptions {
  id: number;
  open: boolean;
}

interface ToastContextValue {
  toast: (opts: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Tone → icon-tile colors (per `.toast-ic` / `.toast--*` in dash.css). */
const toneIcon: Record<ToastTone, string> = {
  neutral: 'bg-surface-2 text-muted',
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
};

export interface ToastProviderProps {
  children: ReactNode;
  /** Default auto-dismiss for toasts that don't specify one. */
  duration?: number;
}

export function ToastProvider({
  children,
  duration = 5000,
}: ToastProviderProps): ReactElement {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const toast = useCallback((opts: ToastOptions): void => {
    setToasts((prev) => [
      ...prev,
      { ...opts, id: Date.now() + Math.random(), open: true },
    ]);
  }, []);

  const setOpen = useCallback((id: number, open: boolean): void => {
    setToasts((prev) =>
      open
        ? prev.map((t) => (t.id === id ? { ...t, open } : t))
        : prev.filter((t) => t.id !== id),
    );
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider duration={duration} swipeDirection="down">
        {children}
        {toasts.map((t) => {
          const tone = t.tone ?? 'neutral';
          return (
            <ToastPrimitive.Root
              key={t.id}
              open={t.open}
              duration={t.duration ?? duration}
              onOpenChange={(open) => setOpen(t.id, open)}
              data-tone={tone}
              className={cn(
                'flex min-w-[280px] items-center gap-[11px] rounded-[10px] border border-border-strong bg-surface-3 py-[11px] pl-[13px] pr-[14px] text-[13px] font-semibold text-text shadow-lg',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=open]:slide-in-from-bottom data-[state=closed]:fade-out-0',
                'data-[swipe=end]:animate-out data-[swipe=end]:slide-out-to-bottom',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'grid h-6 w-6 flex-shrink-0 place-items-center rounded-[7px] [&_svg]:size-[15px]',
                  toneIcon[tone],
                )}
              >
                <ToneIcon tone={tone} />
              </span>
              <div className="flex flex-col gap-0.5">
                <ToastPrimitive.Title>{t.title}</ToastPrimitive.Title>
                {t.description ? (
                  <ToastPrimitive.Description className="text-[11.5px] font-normal text-muted">
                    {t.description}
                  </ToastPrimitive.Description>
                ) : null}
              </div>
              <ToastPrimitive.Close
                aria-label="Dismiss"
                className="ml-auto flex-shrink-0 rounded p-0.5 text-faint transition-colors hover:text-text"
              >
                <CloseIcon />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          );
        })}
        <ToastPrimitive.Viewport className="fixed bottom-[22px] left-1/2 z-[200] flex -translate-x-1/2 flex-col items-center gap-[9px] outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>');
  }
  return ctx;
}

function ToneIcon({ tone }: { tone: ToastTone }): ReactElement {
  // info → sparkle (design); everything else → check.
  if (tone === 'info') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2l1.6 6.4L20 10l-6.4 1.6L12 18l-1.6-6.4L4 10l6.4-1.6L12 2z" />
      </svg>
    );
  }
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function CloseIcon(): ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
